import { useRollbarContext } from '@rollbar/react';
import { Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, type ReactElement } from 'react';

import { useSyncDocumentTitle } from '../lib/documentTitle';
import { useMixpanel } from '../lib/mixpanelContext';
import { newrelicBrowser } from '../lib/newrelic';

export function RootLayout(): ReactElement {
    const router = useRouter();
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    useRollbarContext(pathname);
    useSyncDocumentTitle();

    // Hold trackPageView in a ref so the subscribe-effect can keep empty deps
    // and never re-subscribe on identity changes.
    const { trackPageView } = useMixpanel();
    const trackRef = useRef(trackPageView);
    useEffect(() => {
        trackRef.current = trackPageView;
    }, [trackPageView]);

    useEffect((): (() => void) => {
        return router.subscribe('onResolved', (event) => {
            // Intent preloading (defaultPreload: 'intent') can trigger a transition and
            // emit onResolved while the user is only hovering a link, re-reporting the
            // current path.
            if (!event.pathChanged) {
                return;
            }
            // Read the resolved location from the event, not window.location,
            // so two resolutions in one tick can't report a stale path.
            const page = event.toLocation.pathname;
            // The route pattern rather than the path: checkout tokens and OTPs live in the path and grant access, so
            // they must not reach Mixpanel. It also groups page views per screen instead of per record.
            const route = router.matchRoutes(event.toLocation).at(-1)?.fullPath ?? 'unmatched';
            trackRef.current({ page: route });
            newrelicBrowser.setCurrentRouteName(page);
        });
    }, [router]);

    return <Outlet />;
}
